import { plural } from '@cian/utils';
/**
 * Описание здания для секции «Про …».
 *
 * Порт `get_bs_center_description` монолита (`cian/business_shopping_centers/domain/utils.py`):
 * если у здания нет своего описания, оно собирается из его данных. commercial-buildings отдаёт
 * `description` как есть, без генерации, поэтому логика живёт на фронте. Кандидат на перенос в commercial-buildings.
 * Тексты фраз менять только вместе с монолитом — на проде они видны пользователям и поисковикам.
 */
import {
  ACCESS_TYPE_LABELS,
  BUILDING_CLASS_LABELS,
  BUILDING_OBJECT_TYPE_ABBREVIATIONS,
  INFRASTRUCTURE_LABELS,
  PARKING_TYPE_LABELS,
} from 'shared/constants/buildingLabels';
import type { IBuildingCard, TBuildingObjectType } from 'shared/types/buildingCard';
import { getBuildingName } from 'shared/utils/buildingMainBlock/getBuildingName';
import { formatNumber } from 'shared/utils/format';
import { NBSP } from 'shared/constants/text';

const TITLES: Record<TBuildingObjectType, string> = {
  businessCenter: 'Бизнес Центр',
  shoppingCenter: 'Торговый Центр',
  warehouse: 'Складской Комплекс',
};

/** `_get_bs_center_title` */
function getTitle(card: IBuildingCard, type: TBuildingObjectType, short = false): string {
  const typeTitle = short ? BUILDING_OBJECT_TYPE_ABBREVIATIONS[type] : TITLES[type];
  const name = getBuildingName(card);

  return name.toLowerCase().startsWith(typeTitle.toLowerCase()) ? name : `${typeTitle} ${name}`;
}

function countWithDeclension(count: number, titles: [string, string, string]): string {
  return `${count} ${plural(count, titles)}`;
}

/** `_get_bs_center_location` */
function getLocation(card: IBuildingCard, type: TBuildingObjectType): string {
  const components: string[] = [];
  const address = card.address
    .filter(item => item.isFormingAddress)
    .map(item => item.fullName)
    .join(', ');

  if (address) {
    components.push(`находится по адресу ${address}`);
  }

  const undergrounds = card.undergrounds.filter(item => item.name).map(item => `«${item.name}»`);

  if (undergrounds.length) {
    components.push(`расположен недалеко от метро ${undergrounds.join(', ')}`);
  }

  const title = getTitle(card, type);

  return components.length ? `${title} ${components.join(', ')}.` : `${title}.`;
}

/** `_get_bs_center_building` */
function getBuilding(card: IBuildingCard): string | null {
  const components: string[] = [];
  const classLabel = card.buildingClassType ? BUILDING_CLASS_LABELS[card.buildingClassType] : undefined;

  if (card.totalArea) {
    components.push(`площадью ${formatNumber(card.totalArea)} м²`);
  }
  if (card.buildYear) {
    components.push(`${card.buildYear} года постройки`);
  }
  if (card.floorsTo) {
    components.push(`имеет ${countWithDeclension(card.floorsTo, ['этаж', 'этажа', 'этажей'])}`);
  }
  if (classLabel) {
    components.push(`относится к классу ${classLabel}`);
  }

  return components.length ? `Здание ${components.join(', ')}.` : null;
}

/** `_get_bs_center_infrastructure` */
function getInfrastructure(card: IBuildingCard): string {
  const components: string[] = [];

  if (card.parkings.length) {
    const parkings = card.parkings.reduce<string[]>((result, parking) => {
      const typeLabel = parking.type ? PARKING_TYPE_LABELS[parking.type] : undefined;

      if (typeLabel) {
        const places = parking.placesCount
          ? ` на ${countWithDeclension(parking.placesCount, ['место', 'места', 'мест'])}`
          : '';
        result.push(`${typeLabel.toLowerCase()}${places}`);
      }

      return result;
    }, []);

    if (parkings.length) {
      components.push(`есть парковка - ${parkings.join(', ')}`);
    }
  } else {
    components.push('нет парковки');
  }

  const accessLabel = card.accessType ? ACCESS_TYPE_LABELS[card.accessType] : undefined;

  if (accessLabel) {
    components.push(`доступ в здание - ${accessLabel.toLowerCase()}`);
  }
  if (card.totalLiftCount) {
    components.push(countWithDeclension(card.totalLiftCount, ['лифт', 'лифта', 'лифтов']));
  }
  if (card.travelatorCount) {
    components.push(countWithDeclension(card.travelatorCount, ['траволатор', 'траволатора', 'траволаторов']));
  }
  if (card.escalatorCount) {
    components.push(countWithDeclension(card.escalatorCount, ['эскалатор', 'эскалатора', 'эскалаторов']));
  }
  if (card.entranceCount) {
    components.push(countWithDeclension(card.entranceCount, ['вход', 'входа', 'входов']));
  }

  const { infrastructure } = card;
  const infrastructureItems = infrastructure
    ? INFRASTRUCTURE_LABELS.filter(([key]) => infrastructure[key]).map(([, label]) => label)
    : [];

  if (infrastructureItems.length) {
    components.push(`в здании есть ${infrastructureItems.join(', ')}`);
  }

  return `На территории ${components.join(', ')}.`;
}

/** `_get_bs_center_offer_counts` */
function getOfferCounts(card: IBuildingCard, type: TBuildingObjectType): string | null {
  const components: string[] = [];
  const offerTitles: [string, string, string] = ['предложение', 'предложения', 'предложений'];

  if (card.offersRentCount) {
    components.push(`${countWithDeclension(card.offersRentCount, offerTitles)} об аренде`);
  }
  if (card.offersSaleCount) {
    components.push(`${countWithDeclension(card.offersSaleCount, offerTitles)} о продаже`);
  }

  if (!components.length) {
    return null;
  }

  return `В базе коммерческой недвижимости ЦИАН ${components.join(', ')} помещений в ${getTitle(card, type, true)}.`;
}

function getRange(from: number | null, to: number | null): string | null {
  const components: string[] = [];

  if (from) {
    components.push(`от ${formatNumber(from)}`);
  }
  if (to) {
    components.push(`до ${formatNumber(to)}`);
  }

  return components.length ? components.join(' ') : null;
}

/** `_get_bs_center_rent` */
function getRent(card: IBuildingCard): string | null {
  const range = getRange(card.rates.rentFrom, card.rates.rentTo);

  return range ? `Арендная ставка составляет ${range}${NBSP}₽ за м² в год.` : null;
}

/** `_get_bs_center_sale` */
function getSale(card: IBuildingCard): string | null {
  const range = getRange(card.rates.saleFrom, card.rates.saleTo);

  return range ? `Стоимость помещения ${range}${NBSP}₽ за м².` : null;
}

export function getBuildingDescription(card: IBuildingCard): string | null {
  const { type } = card;

  if (card.description || !type) {
    return card.description;
  }

  return [
    getLocation(card, type),
    getBuilding(card),
    getInfrastructure(card),
    getOfferCounts(card, type),
    getRent(card),
    getSale(card),
  ]
    .filter(Boolean)
    .join('\r\n');
}
